// src/pages/Paiement.js
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * Page coordonnées bancaires (FR/EN/NL)
 * - Lit optionnellement ?amount= et ?due= dans l'URL pour afficher Montant & Échéance
 * - Boutons "Copier" pour IBAN / BIC / Référence
 * - Visuel moderne amélioré avec meilleur responsive design
 */
export default function Paiement() {
  const { t, i18n } = useTranslation("payment");
  const [copiedField, setCopiedField] = useState(null);

  const params = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
  const amount = params.get("amount"); // ex: 120
  const due = params.get("due");       // ex: 2025-10-10

  const fields = useMemo(() => ({
    accountName: "BEUNIT ASBL (MKD studio)",
    iban: "BE80 7340 4589 7577",
    bic: "KREDBEBBXXX",
    reference: t("referenceHint"),
  }), [t]);

  const copy = async (text, fieldName) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      prompt(t("copyFallback"), text);
    }
  };

  return (
    <div style={{ 
      background: "linear-gradient(135deg, #0b0a17 0%, #1a1732 100%)", 
      minHeight: "100vh", 
      color: "#f8fafc",
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
    }}>
      {/* Header */}
      <div style={{
        background: "linear-gradient(135deg, #6f4ef6 0%, #8b5cf6 45%, #5b45ff 100%)",
        padding: "40px 24px",
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)"
      }}>
        <div style={{ 
          maxWidth: 960, 
          margin: "0 auto",
          textAlign: "center"
        }}>
          <div style={{ 
            display: "inline-flex", 
            alignItems: "center", 
            gap: 12,
            background: "rgba(255, 255, 255, 0.15)",
            backdropFilter: "blur(10px)",
            borderRadius: 20,
            padding: "8px 16px",
            marginBottom: 16
          }}>
            <span style={{
              background: "#fff", 
              color: "#5b45ff", 
              borderRadius: "50%",
              width: 24,
              height: 24,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 12, 
              fontWeight: 700
            }}>
              M
            </span>
            <span style={{ fontSize: 14, fontWeight: 600 }}>MKD Studio</span>
          </div>
          <h1 style={{ 
            margin: "8px 0 0 0", 
            fontSize: "clamp(1.8rem, 5vw, 2.5rem)", 
            fontWeight: 800,
            lineHeight: 1.2
          }}>
            {t("title")}
          </h1>
          <p style={{ 
            margin: "12px 0 0 0", 
            opacity: 0.95, 
            fontSize: "clamp(1rem, 2.5vw, 1.1rem)",
            maxWidth: 600,
            marginLeft: "auto",
            marginRight: "auto"
          }}>
            {t("intro")}
          </p>
        </div>
      </div>

      {/* Body */}
      <div style={{ 
        maxWidth: 960, 
        margin: "32px auto", 
        padding: "0 20px" 
      }}>

        {/* Montant / Échéance */}
        {(amount || due) && (
          <div style={{
            background: "rgba(255, 255, 255, 0.05)",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            borderRadius: 16,
            padding: 20,
            marginBottom: 24,
            backdropFilter: "blur(10px)"
          }}>
            <div style={{ 
              display: "flex", 
              gap: 16, 
              flexWrap: "wrap",
              justifyContent: "center"
            }}>
              {amount && (
                <div style={{
                  background: "rgba(91, 69, 255, 0.15)", 
                  color: "#d6d4ff", 
                  borderRadius: 12,
                  padding: "12px 20px", 
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  border: "1px solid rgba(91, 69, 255, 0.3)"
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 17C11.45 17 11 16.55 11 16V12C11 11.45 11.45 11 12 11C12.55 11 13 11.45 13 12V16C13 16.55 12.55 17 12 17ZM13 9H11V7H13V9Z" fill="currentColor"/>
                  </svg>
                  <span>{t("amount")}: <span style={{color: "#fff"}}>{amount}€</span></span>
                </div>
              )}
              {due && (
                <div style={{
                  background: "rgba(111, 78, 246, 0.15)", 
                  color: "#d6d4ff", 
                  borderRadius: 12,
                  padding: "12px 20px", 
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  border: "1px solid rgba(111, 78, 246, 0.3)"
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M19 4H18V3C18 2.45 17.55 2 17 2C16.45 2 16 2.45 16 3V4H8V3C8 2.45 7.55 2 7 2C6.45 2 6 2.45 6 3V4H5C3.89 4 3.01 4.9 3.01 6L3 20C3 21.1 3.89 22 5 22H19C20.1 22 21 21.1 21 20V6C21 4.9 20.1 4 19 4ZM19 20H5V10H19V20ZM19 8H5V6H19V8ZM12 14C12 13.45 11.55 13 11 13H7C6.45 13 6 13.45 6 14C6 14.55 6.45 15 7 15H11C11.55 15 12 14.55 12 14ZM16 18C16 17.45 15.55 17 15 17H7C6.45 17 6 17.45 6 18C6 18.55 6.45 19 7 19H15C15.55 19 16 18.55 16 18Z" fill="currentColor"/>
                  </svg>
                  <span>{t("payBy")}: <span style={{color: "#fff"}}>{new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium" }).format(new Date(due))}</span></span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Coordonnées */}
        <div style={{ 
          display: "grid", 
          gap: 20, 
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))" 
        }}>
          {/* Bénéficiaire */}
          <Card
            label={t("accountName")}
            value={fields.accountName}
            onCopy={() => copy(fields.accountName, "accountName")}
            icon="account"
            copied={copiedField === "accountName"}
          />
          {/* IBAN */}
          <Card
            label="IBAN"
            value={fields.iban}
            onCopy={() => copy(fields.iban, "iban")}
            icon="iban"
            copied={copiedField === "iban"}
          />
          {/* BIC */}
          <Card
            label="BIC"
            value={fields.bic}
            onCopy={() => copy(fields.bic, "bic")}
            icon="bic"
            copied={copiedField === "bic"}
          />
          {/* Référence */}
          <Card
            label={t("reference")}
            value={fields.reference}
            onCopy={() => copy(fields.reference, "reference")}
            icon="reference"
            copied={copiedField === "reference"}
          />
        </div>

        {/* Note */}
        <div style={{
          background: "rgba(32, 29, 61, 0.7)",
          border: "1px solid rgba(43, 39, 85, 0.5)",
          color: "#e2e0ff", 
          padding: 24, 
          borderRadius: 16, 
          marginTop: 28,
          backdropFilter: "blur(10px)"
        }}>
          <h3 style={{ 
            margin: "0 0 12px 0", 
            fontSize: 18, 
            color: "#fff",
            display: "flex",
            alignItems: "center",
            gap: 8
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V11H13V17ZM13 9H11V7H13V9Z" fill="#8b5cf6"/>
            </svg>
            {t("importantNote")}
          </h3>
          <p style={{ margin: "0 0 12px 0", lineHeight: 1.6 }}>{t("validationNote")}</p>
          <div style={{ 
            background: "rgba(111, 78, 246, 0.1)", 
            padding: "12px 16px", 
            borderRadius: 8, 
            margin: "12px 0",
            borderLeft: "3px solid #8b5cf6"
          }}>
            <p style={{ margin: 0, lineHeight: 1.5 }}>{t("tenCardNote")}</p>
          </div>
          <div style={{ 
            background: "rgba(111, 78, 246, 0.1)", 
            padding: "12px 16px", 
            borderRadius: 8,
            borderLeft: "3px solid #8b5cf6"
          }}>
            <p style={{ margin: 0, lineHeight: 1.5 }}>{t("monthlyNote")}</p>
          </div>
        </div>

        {/* Support */}
        <div style={{
          textAlign: "center",
          marginTop: 40,
          padding: 24,
          background: "rgba(255, 255, 255, 0.03)",
          borderRadius: 16,
          border: "1px solid rgba(255, 255, 255, 0.05)"
        }}>
          <p style={{ margin: "0 0 16px 0", opacity: 0.8 }}>{t("needHelp")}</p>
          <div style={{ display: "flex", gap: 16, justifyContent: "center", flexWrap: "wrap" }}>
            <a href="mailto:info@mkdstudio.co" style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              background: "rgba(255, 255, 255, 0.1)",
              color: "#fff",
              textDecoration: "none",
              padding: "10px 20px",
              borderRadius: 10,
              fontWeight: 500,
              transition: "all 0.2s ease"
            }}
            onMouseOver={(e) => e.target.style.background = "rgba(255, 255, 255, 0.15)"}
            onMouseOut={(e) => e.target.style.background = "rgba(255, 255, 255, 0.1)"}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 4H4C2.9 4 2.01 4.9 2.01 6L2 18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4ZM20 8L12 13L4 8V6L12 11L20 6V8Z" fill="currentColor"/>
              </svg>
              info@mkdstudio.co
            </a>
            <a href="tel:+32493887288" style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              background: "rgba(111, 78, 246, 0.2)",
              color: "#fff",
              textDecoration: "none",
              padding: "10px 20px",
              borderRadius: 10,
              fontWeight: 500,
              transition: "all 0.2s ease"
            }}
            onMouseOver={(e) => e.target.style.background = "rgba(111, 78, 246, 0.3)"}
            onMouseOut={(e) => e.target.style.background = "rgba(111, 78, 246, 0.2)"}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 15.5C18.8 15.5 17.5 15.3 16.4 14.9C16.3 14.9 16.2 14.9 16.1 14.9C15.8 14.9 15.6 15 15.4 15.2L13.2 17.4C10.4 15.9 8 13.6 6.6 10.8L8.8 8.6C9.1 8.3 9.2 7.9 9.1 7.6C8.7 6.5 8.5 5.2 8.5 4C8.5 3.5 8 3 7.5 3H4C3.5 3 3 3.5 3 4C3 13.4 10.6 21 20 21C20.5 21 21 20.5 21 20V16.5C21 16 20.5 15.5 20 15.5Z" fill="currentColor"/>
              </svg>
              +32 493 88 72 88
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function Card({ label, value, onCopy, icon, copied }) {
  const getIcon = () => {
    switch(icon) {
      case "account":
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z" fill="#8b5cf6"/>
          </svg>
        );
      case "iban":
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 4H4C2.89 4 2.01 4.89 2.01 6L2 18C2 19.11 2.89 20 4 20H20C21.11 20 22 19.11 22 18V6C22 4.89 21.11 4 20 4ZM20 18H4V12H20V18ZM20 8H4V6H20V8Z" fill="#8b5cf6"/>
          </svg>
        );
      case "bic":
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M5 14.5H9.5C10.33 14.5 11 13.83 11 13V10.5C11 9.67 10.33 9 9.5 9H7V11.5H9V12H7V14.5H5ZM5 10.5H7V9H5V10.5ZM13.5 9H16.5C17.33 9 18 9.67 18 10.5V13C18 13.83 17.33 14.5 16.5 14.5H13.5V9ZM15 12H16V10.5H15V12ZM19 14.5H21V9H19V13.5H18.5V9H16.5V14.5H19Z" fill="#8b5cf6"/>
          </svg>
        );
      case "reference":
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M14 2H6C4.9 2 4.01 2.9 4.01 4L4 20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2ZM16 18H8V16H16V18ZM16 14H8V12H16V14ZM13 9V3.5L18.5 9H13Z" fill="#8b5cf6"/>
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <div style={{
      background: "rgba(22, 20, 43, 0.7)",
      border: "1px solid rgba(35, 31, 67, 0.5)",
      borderRadius: 16, 
      padding: 20,
      backdropFilter: "blur(10px)",
      transition: "transform 0.2s ease, box-shadow 0.2s ease",
      height: "100%",
      display: "flex",
      flexDirection: "column"
    }}
    onMouseOver={(e) => {
      e.target.style.transform = "translateY(-4px)";
      e.target.style.boxShadow = "0 10px 25px rgba(0, 0, 0, 0.15)";
    }}
    onMouseOut={(e) => {
      e.target.style.transform = "translateY(0)";
      e.target.style.boxShadow = "none";
    }}
    >
      <div style={{ 
        display: "flex", 
        alignItems: "center", 
        gap: 12,
        marginBottom: 12 
      }}>
        {getIcon()}
        <div style={{ 
          fontSize: 14, 
          opacity: 0.85, 
          fontWeight: 500,
          color: "#d6d4ff"
        }}>
          {label}
        </div>
      </div>
      <div style={{ 
        fontSize: 18, 
        fontWeight: 700, 
        wordBreak: "break-word",
        color: "#fff",
        marginBottom: 16,
        flexGrow: 1
      }}>
        {value}
      </div>
      <div style={{ marginTop: "auto" }}>
        <button
          type="button"
          onClick={onCopy}
          style={{
            background: copied ? "#10b981" : "linear-gradient(135deg, #6f4ef6 0%, #8b5cf6 100%)",
            color: "#fff", 
            border: 0, 
            padding: "10px 16px",
            borderRadius: 10, 
            fontWeight: 600, 
            cursor: "pointer",
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            transition: "all 0.2s ease",
            boxShadow: copied ? "0 4px 14px rgba(16, 185, 129, 0.4)" : "0 4px 14px rgba(111, 78, 246, 0.4)"
          }}
          onMouseOver={(e) => {
            if (!copied) {
              e.target.style.background = "linear-gradient(135deg, #8b5cf6 0%, #6f4ef6 100%)";
              e.target.style.boxShadow = "0 6px 20px rgba(111, 78, 246, 0.5)";
            }
          }}
          onMouseOut={(e) => {
            if (!copied) {
              e.target.style.background = "linear-gradient(135deg, #6f4ef6 0%, #8b5cf6 100%)";
              e.target.style.boxShadow = "0 4px 14px rgba(111, 78, 246, 0.4)";
            }
          }}
        >
          {copied ? (
            <>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 16.17L4.83 12L3.41 13.41L9 19L21 7L19.59 5.59L9 16.17Z" fill="currentColor"/>
              </svg>
              Copié !
            </>
          ) : (
            <>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M16 1H4C2.9 1 2 1.9 2 3V17H4V3H16V1ZM19 5H8C6.9 5 6 5.9 6 7V21C6 22.1 6.9 23 8 23H19C20.1 23 21 22.1 21 21V7C21 5.9 20.1 5 19 5ZM19 21H8V7H19V21Z" fill="currentColor"/>
              </svg>
              Copier
            </>
          )}
        </button>
      </div>
    </div>
  );
}